// Ranking personalizzato stile Etsy/Vinted (lato client).
// Combina: popolarità globale (precalcolata dal backend) + affinità personale
// (categorie/artigiani dalle interazioni) + qualità + freschezza, con penalità
// per prodotti già visti/comprati e boost premium/verificato.
import { sortProductsByAvailability } from './productAvailability';

const AFFINITY_DOMINANCE = 1000;

const productCategoryTokens = (product) => {
  const tokens = new Set();
  if (product.category) tokens.add(String(product.category).toLowerCase());
  const cp = product.customization_options?.categoryPath;
  if (cp && typeof cp === 'object') {
    ['main', 'sub', 'child', 'childSlug', 'subSlug'].forEach((k) => {
      if (cp[k]) tokens.add(String(cp[k]).toLowerCase());
    });
  }
  return tokens;
};

export const computeRankScore = (product, profile) => {
  if (!product) return 0;

  const cats = profile?.categories || [];
  const crafters = profile?.crafters || [];
  const viewed = new Set(profile?.viewed || []);
  const purchased = new Set(profile?.purchased || []);

  // Affinità categoria (match su qualsiasi livello main/sub/child) — peso massimo
  const tokens = productCategoryTokens(product);
  let catMatch = 0;
  for (const c of cats) {
    const slug = String(c.slug || '').toLowerCase();
    if (!slug) continue;
    let hit = tokens.has(slug);
    if (!hit) {
      for (const t of tokens) { if (t.includes(slug) || slug.includes(t)) { hit = true; break; } }
    }
    if (hit) catMatch = Math.max(catMatch, Number(c.weight || 0));
  }

  // Affinità artigiano
  const match = crafters.find((x) => x.id === product.crafter_id);
  const crafterMatch = match ? Number(match.weight || 0) : 0;

  const affinityComponent = catMatch * 2 + crafterMatch * 3;

  // 2a: similarità "Perché hai guardato X"
  let sim = 0;
  const recentTokens = profile?.recent_tokens || [];
  if (recentTokens.length && [...tokens].some((t) => recentTokens.map((x) => String(x).toLowerCase()).includes(t))) {
    sim += 8;
  }
  if (product.crafter_id && (profile?.recent_crafters || []).includes(product.crafter_id)) {
    sim += 5;
  }
  const band = profile?.recent_price_band;
  if (band) {
    const bp = Number(product.base_price || 0);
    if (bp && bp >= band.min && bp <= band.max) sim += 2;
  }
  const totalAffinity = affinityComponent + sim;

  // Base: popolarità + qualità + freschezza
  let base = Number(product.popularity_score || 0);
  base += 2 * Number(product.review_count || 0);
  base += 8 * Number(product.average_rating || 0);
  if (product.created_at) {
    const daysOld = (Date.now() - new Date(product.created_at).getTime()) / (1000 * 60 * 60 * 24);
    if (daysOld < 7) base += 15;
    else if (daysOld < 30) base += 7;
  }

  let mult = 1.0;
  const tier = product.profiles?.tier || product.tier;
  if (tier === 'pro' || tier === 'premium') mult *= 1.15;
  if (product.profiles?.rarifex_verified) mult *= 1.1;
  if (purchased.has(product.id)) mult *= 0.25;
  else if (viewed.has(product.id)) mult *= 0.85;

  // La navigazione domina sempre; popolarità/qualità/freschezza rifiniscono.
  return totalAffinity * AFFINITY_DOMINANCE + base * mult;
};

// Ritorna i prodotti ordinati per punteggio personalizzato, con i non disponibili in fondo.
export const rankProducts = (products, profile) => {
  if (!Array.isArray(products)) return [];
  const scored = products.map((p) => ({ ...p, _rankScore: computeRankScore(p, profile) }));
  return sortProductsByAvailability(scored, (a, b) => {
    if (b._rankScore !== a._rankScore) return b._rankScore - a._rankScore;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });
};

// Carica il profilo di affinità dal backend (una volta per pagina).
export const fetchRankingProfile = async (apiUrl, userId, sessionId) => {
  try {
    const params = new URLSearchParams();
    if (userId) params.set('user_id', userId);
    if (sessionId) params.set('session_id', sessionId);
    const res = await fetch(`${apiUrl}/api/ranking/profile?${params.toString()}`);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

export const isTrackingOptedOut = () => {
  try {
    return localStorage.getItem('rarifex_tracking_optout') === 'true';
  } catch {
    return false;
  }
};
